import React, { useState, useCallback, useEffect } from 'react';
import { useParams, Link, useOutletContext, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  FiArrowLeft, FiBriefcase, FiCpu, FiBarChart2,
  FiFileText, FiAlertCircle, FiZap, FiPhone, FiMapPin, FiLoader, FiInfo, FiCloud
} from 'react-icons/fi';
import { FaFire } from 'react-icons/fa';

import CourtageDetail from './CourtageDetail';
import AcheminementDetail from './AcheminementDetail';
import TaxesDetail from './TaxesDetail';
import TaxesDetailTicgn from './TaxesDetailTicgn';
import { regularisationTotaleEra } from '../../utils/remboursementEra';

const ClientDetailPage = () => {
  const { id: opportunityId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const context = useOutletContext() || {}; 
  const session = context.session; 

  const [clientData, setClientData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [acheminementResults, setAcheminementResults] = useState({});
  const [taxesAnalysisResults, setTaxesAnalysisResults] = useState({});

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || location.state?.defaultTab || 'general';
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = (id) => {
    setActiveTab(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const availableYears = [2023, 2024, 2025, 2026, 2027];

  const [agentLogs, setAgentLogs] = useState([
    { time: '14:32', message: 'Système initialisé', type: 'info' },
    { time: '14:35', message: 'Connexion aux données Enedis établie', type: 'success' },
    { time: '14:36', message: 'Analyse des courbes de charge en cours...', type: 'info' }
  ]);

  // 🔥 FONCTION HELPER POUR OBTENIR L'ID UNIFORME
  const getInstallationId = (installation) => {
    return installation.type === 'pdl' ? installation.id : (installation.pce_id || installation.id);
  };

  const fetchFullClientData = useCallback(async () => {
    if (!opportunityId || !session?.access_token) return;
    
    if (!clientData) setIsLoading(true);
    setError(null);

    try {
      const oppResponse = await axios.get(`/api/opportunities/${opportunityId}`, { 
        headers: { 'Authorization': `Bearer ${session.access_token}` } 
      });
      
      const opportunity = oppResponse.data.data;
      const account = opportunity.accounts || {};
      const accountId = opportunity.account_id;

      if (!accountId) {
        throw new Error("Cette opportunité n'est liée à aucun compte client valide.");
      }

      const [pdlRes, pceRes, contactRes] = await Promise.all([
        axios.get(`/api/pdl?accountId=${accountId}`, { 
          headers: { 'Authorization': `Bearer ${session.access_token}` } 
        }),
        axios.get(`/api/pce?accountId=${accountId}`, { 
          headers: { 'Authorization': `Bearer ${session.access_token}` } 
        }),
        axios.get(`/api/contacts/principal?accountId=${accountId}`, {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
        })
      ]);

      // 🔥 UNIFORMISATION DES DONNÉES
      const pdls = (pdlRes.data?.data || []).map(p => ({ 
        ...p, 
        type: 'pdl',
        uniqueId: p.id // Clé unique pour PDL
      }));
      
      const pces = (pceRes.data?.data || []).map(p => ({ 
        ...p, 
        type: 'pce',
        uniqueId: p.pce_id || p.id // Clé unique pour PCE
      }));
      
      const mainContact = contactRes.data?.data;

      const formattedClientData = {
        id: opportunity.id,
        accountId: accountId,
        // ownerUserId = propriétaire commercial du compte. Utilisé comme `user_id`
        // par les API Python TURPE (/api/v3/calculate-from-pdl, /api/v4/*) qui
        // l'attendent en body. Fallback historique du temps où PDL avait une colonne
        // user_id (supprimée depuis, cf. table accounts.owner_id).
        ownerUserId: account.owner_id || null,
        user_id: account.owner_id || null,
        name: account.company_name || "Entreprise inconnue",
        siret: account.siret || "Non renseigné",
        address: [account.address, account.postal_code, account.city].filter(Boolean).join(', ') || "Adresse non renseignée",
        
        contact: { 
            name: mainContact 
                ? `${mainContact.prenom} ${mainContact.nom}`.trim() 
                : (account.contact_full_name || "Contact principal"),
            
            email: mainContact 
                ? mainContact.email 
                : (account.contact_email || "Email non renseigné"),
            
            phone: mainContact 
                ? (mainContact.telephone_portable || mainContact.telephone_fixe) 
                : (account.contact_phone || "Tél non renseigné"),
            
            role: mainContact 
                ? mainContact.fonction 
                : "Non renseigné"
        }, 
        installations: [...pdls, ...pces]
      };

      setClientData(formattedClientData);

      // 🔥 CHARGEMENT DES RÉSULTATS D'ANALYSE AVEC TOUS LES IDS
      const allInstallationIds = formattedClientData.installations.map(inst => getInstallationId(inst));

      const results = await Promise.allSettled([
        axios.get(`/api/turpe/analysis-summaries?accountId=${accountId}`, { 
          headers: { 'Authorization': `Bearer ${session.access_token}` } 
        }),
        axios.get(`/api/cspe/documents/analysis-summary?accountId=${accountId}`, { 
          headers: { 'Authorization': `Bearer ${session.access_token}` } 
        })
      ]);

      if (results[0].status === 'fulfilled' && results[0].value.data.success) {
        const achemMap = results[0].value.data.data.reduce((acc, summary) => {
          const key = `${summary.pdl_id}-${summary.analysis_year}`;
          acc[key] = {
            // Résumé PARTAGÉ (24/10/2026) : total validé ; null si analyse non concluante / non vérifiée.
            best_saving_eur: summary.resume?.economie_totale_eur ?? null,
            statut_turpe: summary.resume?.statut || null,
            recommended_version: summary.recommended_version_of_use,
          };
          return acc;
        }, {});
        setAcheminementResults(achemMap);
      }

      if (results[1].status === 'fulfilled' && results[1].value.data.success) {
        const taxesMap = results[1].value.data.data.reduce((acc, result) => {
          const year = result.annee_fiscale || new Date(result.exercice_end_date).getFullYear();
          const key = `${result.pdl_id}-${year}`;
          acc[key] = result;
          return acc;
        }, {});
        setTaxesAnalysisResults(taxesMap);
      }

    } catch (err) {
      console.error("Erreur détaillée de chargement:", err);
      setError(err.response?.data?.message || err.message || "Impossible de charger les informations du dossier.");
    } finally {
      setIsLoading(false);
    }
  }, [opportunityId, session?.access_token]);

  useEffect(() => {
    fetchFullClientData();
  }, [fetchFullClientData]);

  const getInstallationStatusForService = (inst, service, year) => {
    const keyId = getInstallationId(inst);
    const analysisKey = `${keyId}-${year}`;

    if (service === 'acheminement') {
      const found = acheminementResults[analysisKey];
      return found ? 'completed' : 'missing';
    }
    if (service === 'taxes') {
      const found = taxesAnalysisResults[analysisKey];
      return found ? 'completed' : 'missing';
    }
    return 'missing';
  };

  // ✅ CARTE OPTIMISÉE POUR ÉCRANS 13 POUCES
  const InstallationCard = ({ installation, selectedYear, acheminementResult, taxesResult }) => {
    const isPdl = installation.type === 'pdl';
    const number = isPdl ? installation.pdl_number : installation.pce_number;
    const power = isPdl ? installation.contract_power : null;

    const borderClass = isPdl ? "border-l-orange-500" : "border-l-sky-500";
    const bgIconClass = isPdl ? "bg-orange-50 text-orange-600" : "bg-sky-50 text-sky-600";

    const getStatusText = (status) => {
        switch (status) {
          case 'completed': return 'OK';
          case 'pending': return 'En cours';
          case 'missing': return '-';
          default: return '-';
        }
    };

    const getStatusBadgeWithColors = (status, label, tabId, analysisResult) => {
        let badgeClass = '';
        switch (status) {
            case 'completed': badgeClass = 'bg-green-100 text-green-700 ring-1 ring-green-600/20'; break;
            case 'pending': badgeClass = 'bg-orange-100 text-orange-700 ring-1 ring-orange-600/20'; break;
            case 'missing': badgeClass = 'bg-gray-100 text-gray-600 ring-1 ring-gray-600/20'; break;
            default: badgeClass = 'bg-gray-50 text-gray-500';
        }

        let savingsAmount = null;
        if (status === 'completed' && analysisResult) {
            if (label === 'Achem.') savingsAmount = analysisResult.best_saving_eur;
            else if (label === 'CSPE') savingsAmount = regularisationTotaleEra(analysisResult); // K + N
        }
      
        return (
            <button
                onClick={() => setActiveTab(tabId)}
                className="flex flex-col items-center space-y-0.5 text-center group min-w-[60px]"
                title={`Aller à l'onglet ${label === 'Achem.' ? 'Acheminement' : label}`}
            >
                <span className={`inline-flex items-center px-1 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider ${badgeClass} transition-all group-hover:scale-105`}>
                    {label}
                </span>
                
                {status === 'completed' && savingsAmount != null ? (
                    <span className="text-[10px] font-bold text-green-700">
                        {Math.abs(savingsAmount).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}€
                    </span>
                ) : (
                    <span className="text-[9px] text-gray-400 group-hover:text-blue-600 transition-colors">
                        {getStatusText(status)}
                    </span>
                )}
            </button>
        );
    };

    const courtageStatus = getInstallationStatusForService(installation, 'courtage', selectedYear);
    const acheminementStatus = getInstallationStatusForService(installation, 'acheminement', selectedYear);
    const taxesStatus = getInstallationStatusForService(installation, 'taxes', selectedYear);
    
    return (
        <div className={`bg-white border border-gray-200 border-l-4 rounded-r-lg p-2 hover:shadow-md transition-all duration-200 ${borderClass}`}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0 flex items-start gap-2">
                    <div className={`p-1.5 rounded-lg ${bgIconClass} flex-shrink-0`}>
                        {isPdl ? <FiZap className="w-3 h-3" /> : <FiCloud className="w-3 h-3" />}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                            <h3 className="font-mono text-xs font-bold text-gray-900 truncate">
                                {number}
                            </h3>
                            {power && (
                                <span className="px-1 py-0.5 bg-gray-100 text-gray-600 rounded text-[9px] font-semibold border border-gray-200">
                                    {power}kVA
                                </span>
                            )}
                        </div>
                        <div className="text-[10px] text-gray-500 truncate">
                            <span className="font-medium text-gray-800">{installation.site_name}</span>
                            {installation.address?.city && <span className="text-gray-400"> • {installation.address.city}</span>}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {getStatusBadgeWithColors(courtageStatus, 'Court.', 'courtage', null)}
                    {getStatusBadgeWithColors(acheminementStatus, 'Achem.', 'acheminement', acheminementResult)}
                    {isPdl
                      ? getStatusBadgeWithColors(taxesStatus, 'CSPE', 'taxes', taxesResult)
                      : getStatusBadgeWithColors(taxesStatus, 'TICGN', 'ticgn', taxesResult)
                    }
                </div>
            </div>
        </div>
    );
  };
  
  if (isLoading) {
    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
            <div className="text-center">
                <FiLoader className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-3" />
                <p className="text-gray-500 text-sm font-medium">Chargement du dossier...</p>
            </div>
        </div>
    );
  }

  if (error || !clientData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-center p-4">
        <div className="max-w-md w-full bg-white p-6 rounded-xl shadow-lg">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <FiAlertCircle className="h-5 w-5 text-red-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Impossible d'afficher le dossier</h2>
          <p className="text-sm text-gray-600 mb-4">{error || "Une erreur inconnue est survenue."}</p>
          <button 
            onClick={() => navigate(location.state?.from || '/dashboard/admin')}
            className="block w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
        >
            Retour au tableau de bord
        </button>
        </div>
      </div>
    );
  }

  const electricityPoints = clientData.installations.filter(inst => inst.type === 'pdl');
  const gasPoints = clientData.installations.filter(inst => inst.type === 'pce');

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
            <div className="py-2">
                <nav className="flex items-center space-x-2 text-xs text-gray-500 mb-1.5">
                    <button 
                        onClick={() => navigate(location.state?.from || '/dashboard/admin')}
                        className="hover:text-gray-900 flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0"
                    >
                        <FiArrowLeft className="w-3 h-3" /> Retour
                    </button>
                    <span className="text-gray-300">/</span>
                    <span className="text-gray-900 font-medium truncate">{clientData.name}</span>
                </nav>
                <div className="flex items-center justify-between">
                    <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white text-sm font-bold">
                            {clientData.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="truncate">{clientData.name}</span>
                    </h1>
                    <div className="text-[10px] text-gray-500 bg-gray-100 px-2 py-1 rounded-full border border-gray-200">
                        <span className="font-mono font-medium text-gray-700">{clientData.siret}</span>
                    </div>
                </div>
            </div>
            
            <div className="flex space-x-4 mt-1 overflow-x-auto scrollbar-hide">
                {[
                { id: 'general', label: 'Vue', icon: FiInfo },
                { id: 'courtage', label: 'Courtage', icon: FiBarChart2 },
                { id: 'acheminement', label: 'Achem.', icon: FiFileText },
                { id: 'taxes', label: 'CSPE', icon: FiZap },
                { id: 'ticgn', label: 'TICGN', icon: FaFire }
                ].map((tab) => (
                <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center space-x-1.5 py-2.5 px-1 border-b-2 font-medium text-xs whitespace-nowrap transition-colors ${
                    activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                    <tab.icon className={`h-3 w-3 ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span>{tab.label}</span>
                </button>
                ))}
            </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4">
       {activeTab === 'general' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-gray-900 flex items-center gap-1.5">
                        <FiBriefcase className="text-blue-600 w-3 h-3" /> Coordonnées
                    </h3>
                </div>
                <div className="p-3 space-y-3">
                    <div>
                        <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Adresse</label>
                        <div className="mt-0.5 flex items-start gap-1.5 text-xs text-gray-700">
                            <FiMapPin className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span className="text-[11px]">{clientData.address}</span>
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Contact Principal</label>
                        <div className="mt-1.5 flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-[10px] uppercase">
                                {clientData.contact.name.charAt(0)}
                            </div>
                            <div>
                                <p className="text-xs font-medium text-gray-900">{clientData.contact.name}</p>
                                <p className="text-[10px] text-gray-500">{clientData.contact.role}</p>
                            </div>
                        </div>
                        <div className="mt-2 space-y-1 ml-9">
                             <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                <FiBriefcase className="w-2.5 h-2.5 text-gray-400" /> 
                                <span className="truncate text-[11px]">{clientData.contact.email}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                <FiPhone className="w-2.5 h-2.5 text-gray-400" /> 
                                <span className="text-[11px]">{clientData.contact.phone}</span>
                            </div>
                        </div>
                    </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-gray-900 flex items-center gap-1.5">
                        <FiCpu className="text-green-600 w-3 h-3" /> Activité IA
                    </h3>
                    <span className="flex h-2 w-2 relative">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                </div>
                <div className="p-0 flex-1 bg-gray-50/50">
                    <div className="max-h-[140px] overflow-y-auto p-3 space-y-2">
                        {agentLogs.map((log, i) => (
                            <div key={i} className="flex gap-2 text-xs">
                                <span className="text-[10px] font-mono text-gray-400 mt-0.5">{log.time}</span>
                                <div className="flex-1">
                                    <p className={`text-[11px] text-gray-700 ${log.type === 'success' ? 'text-green-700 font-medium' : ''}`}>
                                        {log.message}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl shadow-lg text-white p-4 relative overflow-hidden">
                <div className="relative z-10">
                    <h3 className="text-xs font-semibold text-blue-100 flex items-center gap-1.5 mb-4">
                        <FiBarChart2 className="w-3 h-3" /> Potentiel Identifié
                    </h3>
                    <div className="space-y-3">
                        <div>
                            <p className="text-blue-200 text-[10px] uppercase font-semibold">Économies Estimées</p>
                            <p className="text-2xl font-bold mt-0.5">
                                {Math.floor(
                                    Object.values(acheminementResults).reduce((sum, r) => sum + (r.best_saving_eur || 0), 0) +
                                    Object.values(taxesAnalysisResults).reduce((sum, r) => sum + regularisationTotaleEra(r), 0)
                                ).toLocaleString()} €
                            </p>
                        </div>
                        <div>
                            <p className="text-blue-200 text-[10px] uppercase font-semibold">Points de Livraison</p>
                            <p className="text-lg font-medium mt-0.5">{clientData.installations.length} Sites</p>
                        </div>
                    </div>
                </div>
                <div className="absolute right-0 bottom-0 opacity-10 transform translate-x-4 translate-y-4">
                    <FiZap className="w-24 h-24" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50">
                    <div>
                        <h2 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                            <FiBarChart2 className="text-indigo-500 w-3.5 h-3.5" />
                            Analyse par Point de Livraison
                        </h2>
                        <p className="text-[11px] text-gray-500 mt-0.5">État d'avancement pour l'année sélectionnée.</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-700">Année:</label>
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            className="block w-24 pl-2 pr-8 py-1.5 text-xs border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm"
                        >
                            {availableYears.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="p-4 bg-gray-50/50 space-y-5">
                    
                    <div>
                        <div className="flex items-center gap-1.5 mb-2 px-0.5">
                            <div className="p-0.5 bg-orange-100 rounded text-orange-600"><FiZap className="w-3 h-3" /></div>
                            <h3 className="font-bold text-gray-800 text-xs uppercase tracking-wide">
                                Électricité ({electricityPoints.length})
                            </h3>
                        </div>
                        {electricityPoints.length > 0 ? (
                            <div className="grid gap-2">
                                {electricityPoints.map((inst) => {
                                    const instId = getInstallationId(inst);
                                    const acheminementKey = `${instId}-${selectedYear}`;
                                    const taxesKey = `${instId}-${selectedYear}`;
                                    return (
                                        <InstallationCard 
                                            key={instId} 
                                            installation={inst} 
                                            selectedYear={selectedYear}
                                            acheminementResult={acheminementResults[acheminementKey]}
                                            taxesResult={taxesAnalysisResults[taxesKey]}
                                        />
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-xs text-gray-500 italic bg-white p-2 rounded border border-gray-100">Aucun point électrique.</div>
                        )}
                    </div>

                    <div>
                        <div className="flex items-center gap-1.5 mb-2 px-0.5">
                            <div className="p-0.5 bg-sky-100 rounded text-sky-600"><FiCloud className="w-3 h-3" /></div>
                            <h3 className="font-bold text-gray-800 text-xs uppercase tracking-wide">
                                Gaz ({gasPoints.length})
                            </h3>
                        </div>
                        {gasPoints.length > 0 ? (
                            <div className="grid gap-2">
                                {gasPoints.map((inst) => {
                                    const instId = getInstallationId(inst);
                                    const acheminementKey = `${instId}-${selectedYear}`;
                                    const taxesKey = `${instId}-${selectedYear}`;
                                    return (
                                        <InstallationCard 
                                            key={instId} 
                                            installation={inst} 
                                            selectedYear={selectedYear}
                                            acheminementResult={acheminementResults[acheminementKey]}
                                            taxesResult={taxesAnalysisResults[taxesKey]}
                                        />
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-xs text-gray-500 italic bg-white p-2 rounded border border-gray-100">Aucun point gaz.</div>
                        )}
                    </div>

                </div>
            </div>
          </div>
        )}

        {activeTab === 'courtage' && (
            <CourtageDetail clientData={clientData} session={session} />
        )}

        {activeTab === 'acheminement' && (
          <AcheminementDetail
            clientData={clientData}
            session={session}
            analysisResults={acheminementResults}
            onRefresh={fetchFullClientData}
          />
        )}

        {activeTab === 'taxes' && (
          <TaxesDetail
            clientData={clientData}
            session={session}
            analysisResults={taxesAnalysisResults}
            onRefresh={fetchFullClientData}
          />
        )}

        {activeTab === 'ticgn' && (
          <TaxesDetailTicgn
            clientData={clientData}
            session={session}
          />
        )}
      </main>
    </div>
  );
};

export default ClientDetailPage;
